import { Button, MenuItem } from '@carbon/react';
import { useSetAtom } from 'jotai';
import { evalSettingsPanelOpenAtom } from './atoms';

interface EvalSettingsToggleProps {
  /** `'button'` (default, wide title bar) or `'menuItem'` (collapsed into the Actions menu). */
  variant?: 'button' | 'menuItem';
}

/**
 * The "Eval Settings" toggle in the center title bar (mock §1/§2 scoped actions) — opens/closes
 * the right Eval Settings panel. Renders as a plain tertiary text `Button` (wide), or, below the
 * header-collapse breakpoint, as a `MenuItem` folded into the Actions menu (same toggle behavior).
 *
 * Write-only (`useSetAtom`) — it carries no visual open/closed state, so it never subscribes to
 * (or re-renders on) the panel toggling; the panel reads the same atom in `EvalSettingsPanelSection`.
 *
 * @param {EvalSettingsToggleProps} props - Optional `variant` selecting the button vs menu-item form.
 * @return {JSX.Element} The Eval Settings toggle (button or menu item).
 */
export function EvalSettingsToggle({
  variant = 'button',
}: EvalSettingsToggleProps = {}): JSX.Element {
  const setOpen = useSetAtom(evalSettingsPanelOpenAtom);
  const toggle = () => setOpen((prev) => !prev);

  if (variant === 'menuItem') {
    return <MenuItem label="Eval Settings" onClick={toggle} />;
  }

  return (
    <Button kind="tertiary" size="md" onClick={toggle}>
      Eval Settings
    </Button>
  );
}
